/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "zohocrm",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Delete mode",
          "depth": 1,
          "id": "delete-mode"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      img: "img",
      h3: "h3",
      em: "em"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Modules"
              })
            }), _jsx(_components.td, {
              children: "Sync records to standard or custom modules in Zoho CRM, such as Leads, Contacts, or Deals."
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "After selecting Zoho CRM from our Destinations catalog, you will be prompted to\ninitiate an OAuth flow. Connect Zoho CRM via OAuth by granting permission to the Hightouch app."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zohocrm-oauth-start.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Select the Zoho CRM module you want to sync to. Hightouch lists modules that you can create records in, including custom modules. Activity modules, such as Tasks, Calls, and Events, aren't available."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Upsert"
        }), " creates new records in the module and keeps their fields up to date."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your Zoho CRM workspace by using Zoho's ", _jsx(_components.em, {
          children: "System-defined\nDuplicate Check Fields"
        }), " or an ", _jsx(_components.em, {
          children: "External ID"
        }), " you set for a module in your workspace settings."]
      }), "\n", _jsxs(_components.p, {
        children: ["To know more about each modules's ", _jsx(_components.em, {
          children: "Sytem-defined Duplicate Check Fields"
        }), ", refer to the ", _jsx(_components.em, {
          children: "Sytem-defined Duplicate Check Fields"
        }), "\nsection in the ", _jsx(_components.a, {
          href: "https://www.zoho.com/crm/developer/docs/api/v2/upsert-records.html",
          children: "Upsert Records API"
        }), ". Additionally, to learn\nabout creating an ", _jsx(_components.em, {
          children: "External_ID"
        }), ", refer to ", _jsx(_components.a, {
          href: "https://www.zoho.com/crm/developer/docs/api/v2/records-api-ext-id-overview.html",
          children: "this Zoho docs article"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zohocrm-recordmatching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your model to Zoho's modules. Ensure the module's ", _jsx(_components.em, {
          children: "mandatory field"
        }), "\nis included in the sync and the model columns representing the module fields are of the correct data type."]
      }), "\n", _jsxs(_components.p, {
        children: ["To know more about each module's ", _jsx(_components.em, {
          children: "mandatory fields"
        }), ", refer to the ", _jsx(_components.em, {
          children: "System-defined mandatory fields for\neach module"
        }), " section in ", _jsx(_components.a, {
          href: "https://www.zoho.com/crm/developer/docs/api/v2/insert-records.html",
          children: "this Zoho docs page"
        }), ". Additionally,\nto know more about module field types, refer to the ", _jsx(_components.em, {
          children: "Sample Attributes"
        }), " section."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zohocrm-fieldmappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.p, {
        children: "You can choose what Hightouch's behavior is when records leave the query result set. The default is doing nothing, but you can also set Hightouch to delete the Zoho CRM record."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zohocrm-delete-mode.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
